import React from 'react';
import { Button, ButtonGroup, Collapse, Typography } from '@material-ui/core';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useUpdateBuyOrder } from 'services/buy-process';
import { Form } from '../../../form';
import { FormSectionInfo } from '../../../form/components';
import { RHFRadioGroup } from '../../../../packages/react-hook-form/radio-group/radio-group';
import { RadioLabelled } from '../../../../packages/radio-labelled';
import { PersonInput } from '../../../person-input';
import { useBuyProcessForm } from '../../buy-process-form-utils';
import { RootState } from '../../../../global';
import { saveEditAuditor } from '../../../admin/buy-process/buy-order-edit/buy-order-edit-redux';
import { FormSectionContainer } from '../../../form/components/form-section-container/form-section-container';
import {
  onGoBackInForm,
  onGoForwardInForm,
  useSaveBuyOrderFormState,
  useSelectPeople,
} from '../../use-buy-process-form';
import { useIsAdminOnBuyOrderEditForm } from '../../../../utils/buy-order/use-is-admin-on-buy-order-edit-form';
import { BuyProcessFormNextButton } from '../buy-process-form-next-button/buy-process-form-next-button';
import { BuyProcessFormAdminSubmitButton } from '../buy-process-form-admin-submit-button/buy-process-form-admin-submit-button';
import { selectBuyAuditorPersonFactory } from '../../../../services/buy-process/selectors';
import { AuditorFormState } from './auditor-types';
import {
  AuditorOptionLabel,
  AuditorOptions,
  auditorValidationSchema,
} from './auditor-utils';
import { AuditorCompanyInput } from './components/auditor-company-input';

const useStyles = makeStyles((theme) => ({
  subSection: {
    marginTop: theme.spacing(4),
  },
}));

function useBuyProcessAuditor() {
  const { setRequestedStep, id } = useBuyProcessForm();
  const { auditorFirm, auditorOption } = useSelector(
    (state: RootState) => state.buyOrderEdit[id].auditor,
  );

  const auditorPerson = useSelector(selectBuyAuditorPersonFactory(id));

  const formMethods = useForm<AuditorFormState>({
    defaultValues: {
      auditorOption,
      auditorPerson,
      auditorFirm,
    },
    resolver: yupResolver(auditorValidationSchema),
    mode: 'onBlur',
  });

  useSaveBuyOrderFormState(
    saveEditAuditor,
    formMethods.getValues(),
    formMethods.trigger,
  );

  const auditorOptionSelected = formMethods.watch('auditorOption');

  const people = useSelectPeople(id);

  const dispatch = useDispatch();
  const isAdmin = useIsAdminOnBuyOrderEditForm();
  const [updateBuyOrder, updateBuyOrderMeta] = useUpdateBuyOrder(id);

  return {
    isUpdating: updateBuyOrderMeta.isLoading,
    onSubmit: formMethods.handleSubmit((data) => {
      dispatch(saveEditAuditor({ ...data, id }));
      updateBuyOrder();

      if (!isAdmin) {
        setRequestedStep((s) => s + 1);
      }
    }),
    onForward: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoForwardInForm(setRequestedStep, e),
    onBack: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoBackInForm(setRequestedStep, formMethods.trigger, e),
    formMethods,
    auditorOptionSelected,
    people,
  };
}

export const BuyProcessFormAuditor = () => {
  const classes = useStyles();
  const {
    onSubmit,
    formMethods,
    auditorOptionSelected,
    people,
    onBack,
    onForward,
    isUpdating,
  } = useBuyProcessAuditor();

  return (
    <Form onSubmit={onSubmit}>
      <div>
        <FormSectionInfo.InfoBoxTitled title="Revisor">
          <Typography>
            Kravet på att ha en revisor är borttaget för mindre aktiebolag. De
            lagerbolag vi säljer är vid förvärvet utan revisor. <br /> <br />
            Om du väljer att ha revisor, stäm av om det är han/hon som är
            revisor personligen, eller hans/hennes bolag. På ett större
            revisionsbolag är det ofta firman som är revisor och din
            kontaktperson blir då huvudansvarig revisor.
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer>
          <RHFRadioGroup
            label="Vill ni registrera en revisor?"
            control={formMethods.control}
            name="auditorOption"
            defaultValue=""
          >
            <RadioLabelled
              value={AuditorOptions.no}
              label={AuditorOptionLabel[AuditorOptions.no]}
            />
            <RadioLabelled
              value={AuditorOptions.person}
              label={AuditorOptionLabel[AuditorOptions.person]}
            />
            <RadioLabelled
              value={AuditorOptions.company}
              label={AuditorOptionLabel[AuditorOptions.company]}
            />
          </RHFRadioGroup>

          <Collapse
            in={
              auditorOptionSelected === 'person'
              || auditorOptionSelected === 'company'
            }
          >
            <FormSectionContainer.SubSection
              className={classes.subSection}
              title="Huvudansvarig Revisor:"
            >
              <FormProvider {...formMethods}>
                <PersonInput
                  people={people}
                  fieldName="auditorPerson"
                  nameAugmentFunction={(name) => `auditorPerson.${name}`}
                />
              </FormProvider>
            </FormSectionContainer.SubSection>
          </Collapse>

          <Collapse in={auditorOptionSelected === 'company'}>
            <FormSectionContainer.SubSection
              className={classes.subSection}
              title="Revisionsbolag:"
            >
              <FormProvider {...formMethods}>
                <AuditorCompanyInput />
              </FormProvider>
            </FormSectionContainer.SubSection>
          </Collapse>
        </FormSectionContainer>
      </div>

      <ButtonGroup>
        <Button onClick={onBack}>Tillbaka</Button>
        <BuyProcessFormNextButton onClick={onForward}>
          Nästa
        </BuyProcessFormNextButton>
        <BuyProcessFormAdminSubmitButton loading={isUpdating}>
          Spara
        </BuyProcessFormAdminSubmitButton>
      </ButtonGroup>
    </Form>
  );
};
